// Timing and hit detection utilities

export type HitQuality = 'perfect' | 'good' | 'ok' | 'bad';

// Hit zone dimensions (pixels)
export const HIT_ZONE = {
  innerWidth: 60,
  outerWidth: 120,
  height: 70,
} as const;

// Scoring zones derived from hit zone dimensions (see docs/scoring.md)
export const SCORING_ZONES = {
  perfect: (HIT_ZONE.innerWidth / 2) * 0.9,
  good: (HIT_ZONE.innerWidth + HIT_ZONE.outerWidth) / 4,
  ok: (HIT_ZONE.outerWidth / 2) * 1.1,
} as const;

// Difficulty only controls scroll speed
export interface DifficultyConfig {
  name: string;
  scrollSpeed: number;  // pixels per second
}

export const DIFFICULTIES: Record<string, DifficultyConfig> = {
  easy: {
    name: 'Easy',
    scrollSpeed: 100,
  },
  medium: {
    name: 'Medium',
    scrollSpeed: 150,
  },
  hard: {
    name: 'Hard',
    scrollSpeed: 220,
  },
  expert: {
    name: 'Expert',
    scrollSpeed: 300,
  },
};

// Fixed morse spacing in pixels (standard ratios, never changes)
// Morse code timing ratios:
// - Dot = 1 unit
// - Dash = 3 units
// - Gap within letter = 1 unit
// - Gap between letters = 3 units
// - Gap between words = 7 units
export const MORSE_UNIT_PIXELS = 40;

export const MORSE_SPACING = {
  dotWidth: MORSE_UNIT_PIXELS,           // 1 unit
  dashWidth: MORSE_UNIT_PIXELS * 3,      // 3 units
  symbolGap: MORSE_UNIT_PIXELS,          // 1 unit
  letterGap: MORSE_UNIT_PIXELS * 3,      // 3 units
  wordGap: MORSE_UNIT_PIXELS * 7,        // 7 units
  noteHeight: 32,
} as const;

export function getHitQualityByDistance(pixelOffset: number): HitQuality {
  const absOffset = Math.abs(pixelOffset);
  if (absOffset <= SCORING_ZONES.perfect) return 'perfect';
  if (absOffset <= SCORING_ZONES.good) return 'good';
  if (absOffset <= SCORING_ZONES.ok) return 'ok';
  return 'bad';
}

/**
 * Get multiplier based on current streak
 */
export function getStreakMultiplier(streak: number): number {
  if (streak >= 50) return 4;
  if (streak >= 25) return 3;
  if (streak >= 10) return 2;
  return 1;
}

/**
 * Calculate the timing threshold for distinguishing dots from dashes.
 * Durations below this are dots, above are dashes.
 * Threshold is 2 morse units (midpoint between 1-unit dot and 3-unit dash).
 */
export function getInputThresholdMs(difficulty: DifficultyConfig): number {
  const THRESHOLD_UNITS = 2;
  return (MORSE_UNIT_PIXELS * THRESHOLD_UNITS / difficulty.scrollSpeed) * 1000;
}
